import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { useJobs } from '../../jobs/JobsContext';
import DetalleEdicion from './DetalleEdicion';
import { Modal } from '../../components/Modal';
import {
  EDITION_TYPE_LABEL,
  fmtFecha,
  STATUS_CLASE,
  STATUS_LABEL,
  traducirError,
  type EditionListItem,
  type EditionStatus,
  type EditionType,
  type NewsletterJob,
} from './tipos';

const TIPOS: EditionType[] = ['alerta', 'boletin'];
const ESTADOS: EditionStatus[] = ['draft', 'in_review', 'approved', 'sent'];

export default function SeccionEdiciones() {
  const [ediciones, setEdiciones] = useState<EditionListItem[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  const [filtroTipo, setFiltroTipo] = useState<EditionType | ''>('');
  const [filtroEstado, setFiltroEstado] = useState<EditionStatus | ''>('');

  const [seleccionada, setSeleccionada] = useState<string | null>(null);

  // Estado de la generación en curso.
  const { seguir } = useJobs();
  const [previewInicial, setPreviewInicial] = useState(false);
  const [eliminando, setEliminando] = useState<string | null>(null);
  const [confirmarEliminar, setConfirmarEliminar] = useState<EditionListItem | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobEstado, setJobEstado] = useState<string | null>(null);
  const [errorGen, setErrorGen] = useState<string | null>(null);
  // Corrida histórica: Víctor se para en este día (vacío = hoy).
  const [fechaCorrida, setFechaCorrida] = useState('');

  const filtroTipoId = useId();
  const filtroEstadoId = useId();

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const params = new URLSearchParams();
      if (filtroTipo) params.set('type', filtroTipo);
      if (filtroEstado) params.set('status', filtroEstado);
      const qs = params.toString();
      const data = await apiFetch<{ editions: EditionListItem[] }>(
        `/api/editions${qs ? `?${qs}` : ''}`,
      );
      setEdiciones(data.editions);
    } catch (err) {
      setErrorCarga(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, [filtroTipo, filtroEstado]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Polling del job de generación cada ~3s hasta done/error.
  useEffect(() => {
    if (!jobId) return;
    let cancelado = false;

    const consultar = async () => {
      try {
        const data = await apiFetch<{ job: NewsletterJob }>(`/api/newsletter/jobs/${jobId}`);
        if (cancelado) return;
        const job = data.job;
        if (job.status === 'done') {
          setJobId(null);
          setJobEstado(null);
          void cargar();
        } else if (job.status === 'error') {
          setJobId(null);
          setJobEstado(null);
          setErrorGen(job.error || 'La generación falló.');
        } else {
          setJobEstado(job.status);
        }
      } catch (err) {
        if (cancelado) return;
        setJobId(null);
        setJobEstado(null);
        setErrorGen(traducirError(err));
      }
    };

    void consultar();
    const iv = setInterval(() => void consultar(), 3000);
    return () => {
      cancelado = true;
      clearInterval(iv);
    };
  }, [jobId, cargar]);

  async function generar(type: EditionType) {
    if (jobId) return;
    // El boletín SIEMPRE consolida la semana calendario anterior (lunes→domingo), así que
    // solo puede generarse parado en un lunes — regla de la casa.
    if (type === 'boletin') {
      const fecha = fechaCorrida || new Date().toISOString().slice(0, 10);
      const esLunes = new Date(`${fecha}T12:00:00Z`).getUTCDay() === 1;
      if (!esLunes) {
        setErrorGen(
          fechaCorrida
            ? 'El boletín se genera parado en un LUNES: cubre la semana calendario anterior (lunes a domingo). Elige un lunes en "Como si fuera el".'
            : 'Hoy no es lunes. El boletín cubre la semana calendario anterior, así que elige un lunes en "Como si fuera el".',
        );
        return;
      }
    }
    setErrorGen(null);
    setJobEstado('En cola…');
    try {
      const data = await apiFetch<{ job: NewsletterJob }>('/api/newsletter/run', {
        method: 'POST',
        body: JSON.stringify({ type, scan_date: fechaCorrida || null }),
      });
      setJobId(data.job.id);
      // Seguimiento global: el toast persiste al navegar y notifica al terminar.
      seguir(data.job.id);
    } catch (err) {
      setJobEstado(null);
      setErrorGen(traducirError(err));
    }
  }

  const generando = jobId !== null;

  async function eliminarEdicion(id: string) {
    setEliminando(id);
    try {
      await apiFetch(`/api/editions/${id}`, { method: 'DELETE' });
      setConfirmarEliminar(null);
      await cargar();
    } catch (err) {
      setErrorGen(traducirError(err));
      setConfirmarEliminar(null);
    } finally {
      setEliminando(null);
    }
  }

  if (seleccionada) {
    return (
      <DetalleEdicion
        editionId={seleccionada}
        onVolver={() => setSeleccionada(null)}
        onCambio={cargar}
        previewInicial={previewInicial}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm font-light text-gris">
          Genera, revisa y aprueba las ediciones producidas por el pipeline de agentes.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {/* Víctor viaja en el tiempo: la corrida se para en este día (vacío = hoy) */}
          <label className="flex items-center gap-2 text-xs font-normal text-gris" title="Corrida histórica: Víctor lee las fuentes como si fuera este día. Para boletines solo vale un lunes (cubren la semana anterior, lunes a domingo).">
            <span className="whitespace-nowrap">Como si fuera el</span>
            <input
              type="date"
              value={fechaCorrida}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setFechaCorrida(e.target.value)}
              disabled={generando}
              className="campo !h-9 w-[150px] !text-[13px]"
              aria-label="Fecha de la corrida (opcional, vacío = hoy)"
            />
            {fechaCorrida && (
              <button type="button" onClick={() => setFechaCorrida('')} disabled={generando} className="text-gris hover:text-azul-noche" aria-label="Volver a hoy">
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            )}
          </label>
          {TIPOS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => void generar(t)}
              disabled={generando}
              className="inline-flex items-center gap-2 rounded-lg bg-azul-profundo px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M13 3 4 14h7l-2 7 9-11h-7l2-7Z" />
              </svg>
              Generar {EDITION_TYPE_LABEL[t].toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {generando && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-3 rounded-2xl border border-celeste bg-celeste/20 px-5 py-4"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-5 w-5 animate-spin text-azul-profundo"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M12 3a9 9 0 1 0 9 9" />
          </svg>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-azul-profundo">
              Generando… (esto puede tardar varios minutos)
            </p>
            {jobEstado && (
              <p className="text-xs font-normal text-azul-profundo/70">Estado: {jobEstado}</p>
            )}
          </div>
          {jobId && (
            <Link
              to={`/newsletter/corrida/${jobId}`}
              className="btn-primario !h-8 shrink-0 !px-3.5 !text-[13px]"
            >
              Ver corrida en vivo
            </Link>
          )}
        </div>
      )}

      {errorGen && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {errorGen}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col">
          <label
            htmlFor={filtroTipoId}
            className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-gris"
          >
            Tipo
          </label>
          <select
            id={filtroTipoId}
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value as EditionType | '')}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-texto outline-none transition-colors duration-150 focus:border-azul-profundo focus:ring-2 focus:ring-azul-profundo/25"
          >
            <option value="">Todos</option>
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {EDITION_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label
            htmlFor={filtroEstadoId}
            className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-gris"
          >
            Estado
          </label>
          <select
            id={filtroEstadoId}
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as EditionStatus | '')}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-texto outline-none transition-colors duration-150 focus:border-azul-profundo focus:ring-2 focus:ring-azul-profundo/25"
          >
            <option value="">Todos</option>
            {ESTADOS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {cargando ? (
          <div
            role="status"
            aria-live="polite"
            className="px-6 py-16 text-center text-sm font-light uppercase tracking-[0.2em] text-gris"
          >
            Cargando ediciones…
          </div>
        ) : errorCarga ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm text-red-700">{errorCarga}</p>
            <button
              type="button"
              onClick={() => void cargar()}
              className="mt-4 rounded-lg border border-azul-profundo px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
            >
              Reintentar
            </button>
          </div>
        ) : ediciones.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-azul-medio">
              Sin ediciones todavía
            </p>
            <p className="mt-3 text-sm font-light text-gris">
              Genera una alerta o un boletín para comenzar.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-medium uppercase tracking-[0.15em] text-gris">
                  <th scope="col" className="px-6 py-3 font-medium">
                    Edición
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Tipo
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Estado
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Fecha
                  </th>
                  <th scope="col" className="px-6 py-3 text-right font-medium">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ediciones.map((e) => (
                  <tr
                    key={e.id}
                    className="cursor-pointer text-sm transition-colors duration-150 hover:bg-slate-50"
                    onClick={() => setSeleccionada(e.id)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-normal text-texto">
                          {e.title || `${EDITION_TYPE_LABEL[e.type]} N° ${e.edition_number}`}
                        </span>
                        <span className="mt-0.5 text-[11px] font-normal text-gris">
                          {e.generada_por ? `Generada por ${e.generada_por}` : 'Programada · Víctor, el Director'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600">
                        {EDITION_TYPE_LABEL[e.type]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${STATUS_CLASE[e.status]}`}
                      >
                        {STATUS_LABEL[e.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs font-normal text-gris">
                      {fmtFecha(e.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          type="button"
                          title="Ver"
                          aria-label={`Ver ${e.title ?? 'edición'}`}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setPreviewInicial(true);
                            setSeleccionada(e.id);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M2 12s3.5-7.5 10-7.5S22 12 22 12s-3.5 7.5-10 7.5S2 12 2 12Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          title="Editar"
                          aria-label={`Editar ${e.title ?? 'edición'}`}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setPreviewInicial(false);
                            setSeleccionada(e.id);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          title={e.status === 'sent' ? 'Las ediciones enviadas no se eliminan' : 'Borrar'}
                          aria-label={`Borrar ${e.title ?? 'edición'}`}
                          disabled={e.status === 'sent' || eliminando === e.id}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setConfirmarEliminar(e);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-red-600 transition-colors duration-150 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600 disabled:cursor-not-allowed disabled:opacity-35"
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z" />
                            <path d="M10 11v6M14 11v6" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmarEliminar && (
        <Modal titulo="Borrar edición" onCerrar={() => setConfirmarEliminar(null)}>
          <p className="text-sm font-normal leading-relaxed text-texto">
            ¿Seguro que quieres borrar{' '}
            <span className="font-medium text-azul-profundo">
              {confirmarEliminar.title || `${EDITION_TYPE_LABEL[confirmarEliminar.type]} N° ${confirmarEliminar.edition_number}`}
            </span>
            ? Se eliminarán sus items y aprobaciones. Esta acción no se puede deshacer.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => setConfirmarEliminar(null)} className="btn-fantasma">
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void eliminarEdicion(confirmarEliminar.id)}
              disabled={eliminando !== null}
              className="btn-peligro"
            >
              {eliminando ? 'Borrando…' : 'Borrar edición'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
